import axiosInstance from "../../../utils/axios";
import { ROLE } from "../../../utils/Constants";

const options = { role: ROLE.SUPER_ADMIN };
const path = "admin/reports/live-calc";

/**
 * Get all club admins for the selection dropdown.
 */
export const getClubs = async () => {
  return await axiosInstance(options).get(`${path}/clubs`);
};

/**
 * Get all matches for a specific club admin.
 * @param {string} clubAdminId
 */
export const getMatches = async (clubAdminId) => {
  return await axiosInstance(options).get(`${path}/matches?clubAdminId=${clubAdminId}`);
};

/**
 * Get all teams for a specific club admin.
 * @param {string} clubAdminId
 */
export const getTeams = async (clubAdminId) => {
  return await axiosInstance(options).get(`${path}/teams?clubAdminId=${clubAdminId}`);
};

/**
 * Get all players for a club admin, optionally filtered by team.
 * @param {string} clubAdminId
 * @param {string|null} teamId
 */
export const getPlayers = async (clubAdminId, teamId = null) => {
  const teamParam = teamId ? `&teamId=${teamId}` : "";
  return await axiosInstance(options).get(`${path}/players?clubAdminId=${clubAdminId}${teamParam}`);
};

/**
 * Get overview stats (total matches, teams, players, parents + live breakdown).
 * @param {string} clubAdminId
 */
export const getStats = async (clubAdminId) => {
  return await axiosInstance(options).get(`${path}/stats?clubAdminId=${clubAdminId}`);
};

/**
 * Get full live match analysis (team stats, leaderboard, momentum, quarter report, event log).
 * @param {string} matchId
 * @param {string|null} teamId
 */
export const getMatchAnalysis = async (matchId, teamId = null, nocache = false) => {
  const teamParam = teamId ? `&teamId=${teamId}` : "";
  const nocacheParam = nocache ? `&nocache=true&_t=${Date.now()}` : "";
  return await axiosInstance(options).get(`${path}/match-analysis?matchId=${matchId}${teamParam}${nocacheParam}`);
};

/**
 * Get full team insights (re-uses existing admin/reports/team-insights endpoint).
 * @param {string} teamId
 * @param {string|null} matchId
 * @param {string|null} playerId
 * @param {string} tab - overview|shooting|defence|possession|quarters|chains_scoreboard|all
 */
export const getTeamInsights = async (teamId, matchId = null, playerId = null, tab = "overview") => {
  const matchParam = matchId ? `&matchId=${matchId}` : "";
  const playerParam = playerId ? `&playerId=${playerId}` : "";
  return await axiosInstance(options).get(
    `admin/reports/team-insights?teamId=${teamId}${matchParam}${playerParam}&tab=${tab}`
  );
};

/**
 * Get historic stats (Win/Draw/Loss, Avg Goals, GP% Trend for last 5 matches).
 * @param {string} teamId
 * @param {string|null} playerId
 * @param {string} year - "all_time" or a specific year
 */
export const getHistory = async (teamId, playerId = null, year = "all_time", startDate = "", endDate = "", nocache = false, clubAdminId = null) => {
  const teamParam = teamId ? `teamId=${teamId}` : "";
  const adminParam = clubAdminId ? `clubAdminId=${clubAdminId}` : "";
  const firstParam = teamParam || adminParam;
  const secondParam = teamParam && adminParam ? `&${adminParam}` : "";
  const playerParam = playerId ? `&playerId=${playerId}` : "";
  const startParam = startDate ? `&startDate=${startDate}` : "";
  const endParam = endDate ? `&endDate=${endDate}` : "";
  const nocacheParam = nocache ? `&nocache=true&_t=${Date.now()}` : "";
  return await axiosInstance(options).get(`${path}/history?${firstParam}${secondParam}${playerParam}&year=${year}${startParam}${endParam}${nocacheParam}`);
};

/**
 * Get deep player PIR card report matching the mobile app screens.
 * Includes PIR breakdown, Hot Streaks, Best Quarter, position-specific metrics.
 * @param {string} teamId
 * @param {string} playerId
 * @param {string|null} matchId
 */
export const getPlayerReport = async (teamId, playerId, matchId = null, nocache = false) => {
  const matchParam = matchId ? `&matchId=${matchId}` : "";
  const nocacheParam = nocache ? `&nocache=true&_t=${Date.now()}` : "";
  return await axiosInstance(options).get(`${path}/player-report?teamId=${teamId}&playerId=${playerId}${matchParam}${nocacheParam}`);
};
